import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { MapPin, Navigation, Loader2, Check, X, Search } from 'lucide-react';
import { toast } from 'sonner';

/**
 * LocationPicker - A component to pick location via GPS or manual map search
 * Uses OpenStreetMap Nominatim for geocoding (free, no API key needed)
 */
export default function LocationPicker({ 
  onLocationSelect, 
  initialLatitude, 
  initialLongitude, 
  initialAddress,
  className 
}) {
  const [latitude, setLatitude] = useState(initialLatitude || null);
  const [longitude, setLongitude] = useState(initialLongitude || null);
  const [address, setAddress] = useState(initialAddress || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationMethod, setLocationMethod] = useState(null); // 'gps' or 'manual'

  // Notify parent when location changes
  useEffect(() => {
    if (latitude && longitude) {
      onLocationSelect({ latitude, longitude, address });
    }
  }, [latitude, longitude, address, onLocationSelect]);

  // Get current GPS location
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        setLatitude(lat);
        setLongitude(lon);
        setLocationMethod('gps');

        // Reverse geocode to get address
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&addressdetails=1`,
            { headers: { 'User-Agent': '1Clin/1.0' } }
          );
          const data = await response.json();
          const fullAddress = data.display_name || `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
          setAddress(fullAddress);
          toast.success('Location detected successfully');
        } catch (err) {
          setAddress(`${lat.toFixed(6)}, ${lon.toFixed(6)}`);
          toast.success('Location detected (address lookup failed)');
        }
        setIsGettingLocation(false);
      },
      (error) => {
        setIsGettingLocation(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Location permission denied. Please enable location access.');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Location information unavailable');
            break;
          case error.TIMEOUT:
            toast.error('Location request timed out');
            break;
          default:
            toast.error('Failed to get location');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);

  // Search for address using Nominatim
  const searchAddress = useCallback(async () => {
    if (!searchQuery.trim() || searchQuery.length < 3) {
      toast.error('Please enter at least 3 characters to search');
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&format=json&limit=5&countrycodes=in`,
        { headers: { 'User-Agent': '1Clin/1.0' } }
      );
      const data = await response.json();
      setSearchResults(data);
      if (data.length === 0) {
        toast.info('No results found. Try a different search term.');
      }
    } catch (err) {
      toast.error('Search failed. Please try again.');
      setSearchResults([]);
    }
    setIsSearching(false);
  }, [searchQuery]);

  // Select an address from search results
  const selectAddress = (result) => {
    setLatitude(parseFloat(result.lat));
    setLongitude(parseFloat(result.lon));
    setAddress(result.display_name);
    setLocationMethod('manual');
    setSearchResults([]);
    setSearchQuery('');
    toast.success('Location selected');
  };

  // Clear location
  const clearLocation = () => {
    setLatitude(null);
    setLongitude(null);
    setAddress('');
    setLocationMethod(null);
    setSearchResults([]);
    onLocationSelect({ latitude: null, longitude: null, address: '' });
  };

  return (
    <div className={className}>
      <Label className="flex items-center gap-2 mb-2">
        <MapPin className="w-4 h-4 text-teal-600" />
        Clinic Location
        <span className="text-xs text-slate-400 font-normal">(For MR check-in verification)</span>
      </Label>

      {/* Location Display */}
      {latitude && longitude ? (
        <Card className="border-teal-200 bg-teal-50/50 mb-3">
          <CardContent className="p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Check className="w-4 h-4 text-teal-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-teal-700">
                    Location Set ({locationMethod === 'gps' ? 'GPS' : 'Manual'})
                  </span>
                </div>
                <p className="text-xs text-slate-600 line-clamp-2">{address}</p>
                <p className="text-[10px] text-slate-400 mt-1">
                  Coordinates: {latitude.toFixed(6)}, {longitude.toFixed(6)}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearLocation}
                className="text-red-500 hover:text-red-600 hover:bg-red-50 p-1"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* GPS Button */}
          <Button
            type="button"
            variant="outline"
            className="w-full h-11 mb-2 border-teal-200 hover:bg-teal-50 text-teal-700"
            onClick={getCurrentLocation}
            disabled={isGettingLocation}
          >
            {isGettingLocation ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Detecting location...
              </>
            ) : (
              <>
                <Navigation className="w-4 h-4 mr-2" />
                Use Current Location (GPS)
              </>
            )}
          </Button>

          {/* Divider */}
          <div className="flex items-center gap-3 my-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400">or search manually</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Manual Search */}
          <div className="flex gap-2">
            <Input
              placeholder="Search address (e.g., MG Road, Bangalore)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchAddress())}
              className="flex-1 h-11"
            />
            <Button
              type="button"
              variant="outline"
              onClick={searchAddress}
              disabled={isSearching}
              className="h-11 px-3"
            >
              {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-2 border rounded-lg overflow-hidden bg-white shadow-sm max-h-48 overflow-y-auto">
              {searchResults.map((result, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => selectAddress(result)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 border-b last:border-b-0 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                    <span className="line-clamp-2">{result.display_name}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Preview Map Link */}
      {latitude && longitude && (
        <a
          href={`https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}&zoom=17`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-teal-600 hover:underline mt-2 inline-block"
        >
          View on Map →
        </a>
      )}
    </div>
  );
}
